/**
 * @file 规则配置管线（ADR-005 五步）。
 *
 * 入口：`createRuleSnapshot(patch)` → 深拷贝 → 规范化(V-1~V-16) → 规范序列化
 * → 哈希(FNV-1a) → 递归冻结。房间创建时调用一次，产出不可变 `RuleSnapshot`。
 *
 * 校验 V-1~V-16 的错误码全部取自 `config.ts` 的 {@link RuleValidationCode}，
 * 前端错误提示据此对齐 GDD §5。变体开启 / 护栏违反一律**显式报错**（不静默忽略）。
 */

import type { RuleConfig, RuleConfigIssue, RuleConfigPatch, RuleSnapshot } from './config';
import { CHENGDU_STANDARD, FAN_KEYS, RULE_CONFIG_VERSION, RULE_SCHEMA_VERSION } from './config';
import { RuleConfigError } from './errors';
import { canonicalStringify } from './json';

type AnyObj = Record<string, unknown>;

function isPlainObject(v: unknown): v is AnyObj {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** 递归深拷贝（配置无函数/日期，纯 JSON 结构即可）。 */
function deepClone<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => deepClone(v)) as unknown as T;
  const out: AnyObj = {};
  for (const k of Object.keys(value as AnyObj)) out[k] = deepClone((value as AnyObj)[k]);
  return out as T;
}

/** 递归冻结（深冻结）。已冻结则短路返回，避免对循环引用重复处理。 */
function deepFreeze<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Object.isFrozen(obj)) return obj;
  for (const k of Object.keys(obj as AnyObj)) deepFreeze((obj as AnyObj)[k]);
  return Object.freeze(obj);
}

/**
 * 以 `base` 为基线合并 `patch`（DeepPartial）。
 *
 * - 只遍历 `base` 的键 → `patch` 中的未知字段被自然忽略（GDD 原则 P4，向前兼容）；
 * - 子对象递归合并，标量 / 数组直接覆盖；
 * - `patch` 中 `undefined` 的字段回落到 `base`。
 */
function mergeConfig(base: AnyObj, patch: AnyObj): AnyObj {
  const out: AnyObj = {};
  for (const key of Object.keys(base)) {
    const bv = base[key];
    const pv = patch[key];
    if (pv === undefined) {
      out[key] = bv;
    } else if (isPlainObject(bv) && isPlainObject(pv)) {
      out[key] = mergeConfig(bv, pv);
    } else {
      out[key] = pv;
    }
  }
  return out;
}

function isInt(n: number): boolean {
  return typeof n === 'number' && Number.isInteger(n);
}
function inRange(n: number, lo: number, hi: number): boolean {
  return isInt(n) && n >= lo && n <= hi;
}

/** 执行 GDD §5 的 V-1~V-16 全部校验，返回问题清单（可同时含 error 与 warning）。 */
function runValidations(config: RuleConfig): RuleConfigIssue[] {
  const issues: RuleConfigIssue[] = [];

  // V-1
  if (config.version !== RULE_CONFIG_VERSION) {
    issues.push({ path: 'version', code: 'UNSUPPORTED_CONFIG_VERSION', message: `version 必须为 ${RULE_CONFIG_VERSION}`, severity: 'error' });
  }
  // V-2
  if (!inRange(config.scoring.baseScore, 1, 100)) {
    issues.push({ path: 'scoring.baseScore', code: 'INVALID_BASE_SCORE', message: '底分须为 [1,100] 整数', severity: 'error' });
  }
  // V-3（[0,99] 放行，M1 运行时再以 M1_ENFORCED_FAN_CAP 封顶）
  if (!inRange(config.scoring.fanCap, 0, 99)) {
    issues.push({ path: 'scoring.fanCap', code: 'INVALID_FAN_CAP', message: '封顶番数须为 [0,99] 整数', severity: 'error' });
  }
  // V-4
  for (const f of ['mingGang', 'anGang', 'baGang'] as const) {
    if (!inRange(config.gangScore[f], 0, 50)) {
      issues.push({ path: `gangScore.${f}`, code: 'INVALID_GANG_SCORE', message: `${f} 须为 [0,50] 整数`, severity: 'error' });
    }
  }
  // V-5
  for (const k of FAN_KEYS) {
    if (!inRange(config.fan[k].fan, 0, 20)) {
      issues.push({ path: `fan.${k}.fan`, code: 'INVALID_FAN_VALUE', message: `${k}.fan 须为 [0,20] 整数`, severity: 'error' });
    }
  }
  // V-6 + reconnectGraceMs 过大告警
  const to = config.timeout;
  for (const f of ['decideQueMs', 'discardMs', 'responseMs', 'qiangGangMs', 'reconnectGraceMs'] as const) {
    if (!inRange(to[f], 1000, 300000)) {
      issues.push({ path: `timeout.${f}`, code: 'INVALID_TIMEOUT', message: `${f} 须为 [1000,300000] 整数(ms)`, severity: 'error' });
    }
  }
  if (to.reconnectGraceMs > 120000) {
    issues.push({ path: 'timeout.reconnectGraceMs', code: 'INVALID_TIMEOUT', message: 'reconnectGraceMs 超过 120s 会抬高 DO 驻留时长与免费额度消耗', severity: 'warning' });
  }
  // V-7
  if (!inRange(config.wall.reservedTiles, 0, 20)) {
    issues.push({ path: 'wall.reservedTiles', code: 'INVALID_RESERVED_TILES', message: '留底牌须为 [0,20] 整数', severity: 'error' });
  }
  // V-8 变体禁止开启
  for (const [name, v] of Object.entries(config.variant) as [string, { enabled: boolean }][]) {
    if (v.enabled) {
      issues.push({ path: `variant.${name}.enabled`, code: 'UNSUPPORTED_RULE', message: `M1 未实现变体「${name}」，禁止开启`, severity: 'error' });
    }
  }
  // V-9 防死锁护栏
  if (config.gang.forbidGangWhenWallEmpty !== true) {
    issues.push({ path: 'gang.forbidGangWhenWallEmpty', code: 'UNSAFE_CONFIG', message: '必须为 true，否则会出现「杠了无牌可补」死锁', severity: 'error' });
  }
  // V-10
  if (config.hu.decompositionPolicy !== 'MAX') {
    issues.push({ path: 'hu.decompositionPolicy', code: 'UNSUPPORTED_RULE', message: 'M1 仅支持 MAX', severity: 'error' });
  }
  // V-11 不实现抢暗杠
  if (config.qiangGang.allowAnGang !== false) {
    issues.push({ path: 'qiangGang.allowAnGang', code: 'UNSUPPORTED_RULE', message: 'M1 不实现抢暗杠', severity: 'error' });
  }
  // V-12 仅原路退还
  if (config.chaDaJiao.gangRefundMode !== 'REFUND_TO_PAYER') {
    issues.push({ path: 'chaDaJiao.gangRefundMode', code: 'UNSUPPORTED_RULE', message: 'M1 仅支持 REFUND_TO_PAYER', severity: 'error' });
  }
  // V-13 已胡玩家不再支付
  if (config.xuezhan.wonPlayerPaysZimo !== false || config.xuezhan.wonPlayerPaysGang !== false) {
    issues.push({ path: 'xuezhan', code: 'UNSUPPORTED_RULE', message: 'M1 已胡玩家不再支付后续自摸/杠分', severity: 'error' });
  }
  // V-14 花猪 reserved
  if (config.chaDaJiao.huaZhuPenaltyFan !== 0) {
    issues.push({ path: 'chaDaJiao.huaZhuPenaltyFan', code: 'UNSUPPORTED_RULE', message: 'M1 花猪 reserved，恒为 0', severity: 'error' });
  }
  // V-15 不实现单局输赢上限
  if (config.scoring.perGameLossCap !== 0) {
    issues.push({ path: 'scoring.perGameLossCap', code: 'UNSUPPORTED_RULE', message: 'M1 不实现单局输赢上限（破坏分数守恒）', severity: 'error' });
  }
  // V-16 明杠仅放杠者支付
  if (config.gangScore.mingGangPayer !== 'DEALER_ONLY') {
    issues.push({ path: 'gangScore.mingGangPayer', code: 'UNSUPPORTED_RULE', message: 'M1 明杠仅放杠者支付', severity: 'error' });
  }
  return issues;
}

/* ─────────────────────────── 公共 API ─────────────────────────── */

/** 返回内置默认规则（成都标准）的**独立深拷贝**。 */
export function defaultRuleConfig(): RuleConfig {
  return deepClone(CHENGDU_STANDARD);
}

/** 规范化：以 `CHENGDU_STANDARD` 补默认值 → 执行 V-1~V-16 → 返回完整配置与问题清单。 */
export function normalizeRuleConfig(patch: RuleConfigPatch): {
  readonly config: RuleConfig;
  readonly issues: readonly RuleConfigIssue[];
} {
  const merged = mergeConfig(CHENGDU_STANDARD as unknown as AnyObj, patch as unknown as AnyObj);
  return { config: merged as unknown as RuleConfig, issues: runValidations(merged as unknown as RuleConfig) };
}

/** 规范序列化：语义相同 → 逐字节相同字符串（键序稳定）。 */
export function canonicalizeRuleConfig(config: RuleConfig): string {
  return canonicalStringify(config);
}

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const FNV_MASK = 0xffffffffffffffffn;

/** UTF-8 字节序列（手写，避免依赖平台 `TextEncoder`）。 */
function utf8Bytes(s: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else if (c >= 0xd800 && c <= 0xdbff) {
      const c2 = s.charCodeAt(++i);
      c = 0x10000 + ((c & 0x3ff) << 10) + (c2 & 0x3ff);
      bytes.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    } else bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
  }
  return bytes;
}

/** 对规范 JSON 求 64 位 FNV-1a 哈希，返回 16 位小写十六进制（非加密，仅用于一致性比对）。 */
export function hashRuleConfig(canonicalJson: string): string {
  let h = FNV_OFFSET;
  for (const b of utf8Bytes(canonicalJson)) {
    h ^= BigInt(b);
    h = (h * FNV_PRIME) & FNV_MASK;
  }
  return h.toString(16).padStart(16, '0');
}

/**
 * 创建房间级不可变规则快照。管线：深拷贝 → 规范化 → 规范序列化 → 哈希 → 冻结。
 * 规范化出现 `error` 级问题时抛 {@link RuleConfigError}。
 */
export function createRuleSnapshot(patch: RuleConfigPatch): RuleSnapshot {
  const { config, issues } = normalizeRuleConfig(patch);
  const errors = issues.filter((i) => i.severity === 'error');
  if (errors.length > 0) {
    throw new RuleConfigError(
      `规则配置非法：发现 ${errors.length} 个错误`,
      errors.map((e) => ({ path: e.path, message: e.message })),
    );
  }
  const canonicalJson = canonicalizeRuleConfig(config);
  const ruleHash = hashRuleConfig(canonicalJson);
  return {
    config: deepFreeze(config),
    ruleHash,
    canonicalJson,
    schemaVersion: RULE_SCHEMA_VERSION,
  };
}
